import { ComponentProps, createContext, ReactNode, useContext, useId } from 'react'
import { CircleAlert } from 'lucide-react'

interface ContextoCampo {
  id: string
  idAyuda: string
  conError: boolean
  conAyuda: boolean
}
const Contexto = createContext<ContextoCampo | null>(null)

/**
 * Campo del kit: etiqueta, control y ayuda o error. Enlaza solo la etiqueta con el
 * control (`htmlFor`/`id`) y la ayuda con `aria-describedby`: basta con poner un
 * <Entrada> o <AreaTexto> adentro.
 *
 * `ancho` (por defecto) usa el modificador del kit para ocupar el contenedor; el kit
 * acota `.dcc-field` a 320px pensando en formularios sueltos.
 */
export function Campo({
  etiqueta, ayuda, error, requerido, ancho = true, children,
}: {
  etiqueta: string
  ayuda?: ReactNode
  error?: string | null
  requerido?: boolean
  ancho?: boolean
  children: ReactNode
}) {
  const id = useId()
  const idAyuda = `${id}-ayuda`
  const clases = ['dcc-field', ancho && 'dcc-field--wide', error && 'dcc-field--error'].filter(Boolean).join(' ')
  return (
    <Contexto.Provider value={{ id, idAyuda, conError: !!error, conAyuda: !!(error || ayuda) }}>
      <div className={clases}>
        <label className="dcc-field__label" htmlFor={id}>
          {etiqueta} {requerido && <span className="dcc-req">*</span>}
        </label>
        {children}
        {error ? (
          <span className="dcc-field__help dcc-field__help--error" id={idAyuda}>
            <CircleAlert className="dcc-i" aria-hidden />
            {error}
          </span>
        ) : (
          ayuda && <span className="dcc-field__help" id={idAyuda}>{ayuda}</span>
        )}
      </div>
    </Contexto.Provider>
  )
}

function useAtributosCampo() {
  const campo = useContext(Contexto)
  if (!campo) return {}
  return {
    id: campo.id,
    'aria-invalid': campo.conError || undefined,
    'aria-describedby': campo.conAyuda ? campo.idAyuda : undefined,
  }
}

/** `ref` llega como prop (React 19), así que sirve para inputs de archivo que hay que limpiar. */
export function Entrada(props: ComponentProps<'input'>) {
  return <input className="dcc-input" {...useAtributosCampo()} {...props} />
}

/** <select> nativo con la caja del kit: accesible y cómodo en el celular (abre el selector del sistema). */
export function Seleccion(props: ComponentProps<'select'>) {
  return <select className="dcc-input seleccion" {...useAtributosCampo()} {...props} />
}

export function AreaTexto(props: ComponentProps<'textarea'>) {
  return <textarea className="dcc-input" rows={4} {...useAtributosCampo()} {...props} />
}
